import { use, useEffect, useState } from 'react';
import { signInWithPopup, GoogleAuthProvider, linkWithPopup, signInWithRedirect, linkWithRedirect, getRedirectResult } from 'firebase/auth';
import { ForwardTo, LoadingSkeleton } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { AuthContext } from '@/lib/context';
import useUser from '@/lib/hooks/use-user';
import { Analytics } from '@/lib/analytics';
import { logError } from '@/lib/error-logging';
import { useIsMobile } from '@/lib/hooks/use-mobile';

export default function LoginPage() {
    const { user, isUserLoading } = useUser();
    const auth = use(AuthContext);
    const isMobile = useIsMobile();
    const [loginError, setLoginError] = useState<string | null>(null);
    const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);

    useEffect(() => {
        // Handle redirect results on mount
        const handleRedirectResult = async () => {
            if (!auth) return;

            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    console.log('Successfully signed in via redirect');
                    Analytics.userLogin('google_redirect');
                    // ForwardTo will happen automatically because of the (user && !user.isAnonymous) check below
                    // which is triggered when onAuthStateChanged fires via useUser hook
                }
            } catch (error: any) {
                console.error('Redirect result error:', error);
                logError(error, {
                    component: 'LoginPage',
                    action: 'redirect_result',
                    additionalInfo: { provider: 'google' }
                });

                if (error.code === 'auth/credential-already-in-use') {
                    setLoginError("This Google account is already linked to another BibleGlen user. Sign in directly to access that account (guest progress will not be merged).");
                } else {
                    setLoginError(error.message);
                }
            } finally {
                setIsProcessingRedirect(false);
            }
        };

        // We only want to check for redirect results if we're on a mobile device
        // or if we explicitly think we're returning from a redirect.
        // Firebase Auth internally handles the check, but we set loading state.
        setIsProcessingRedirect(true);
        handleRedirectResult();
    }, [auth]);

    if (isUserLoading || isProcessingRedirect) {
        return <LoadingSkeleton />;
    }

    if (user && !user.isAnonymous) {
        return <ForwardTo path="/app" />;
    }

    return <>
        <div className="bg-muted/60 flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
                <div className="flex flex-col gap-6">
                    <Card>
                        <CardHeader className="text-center">
                            <CardTitle className="text-xl font-light"><a href="/"><ArrowLeft /></a>Join the Expedition</CardTitle>
                            <CardDescription>Sign in with Google to save your discoveries and access them from any device.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loginError && (
                                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-xs rounded-md">
                                    {loginError}
                                    {loginError.includes('already linked') && (
                                        <Button
                                            variant="link"
                                            className="h-auto p-0 text-xs ml-1 font-bold underline"
                                            onClick={() => {
                                                setLoginError(null);
                                                signInWithPopup(auth, new GoogleAuthProvider());
                                            }}
                                        >
                                            Sign in anyway
                                        </Button>
                                    )}
                                </div>
                            )}
                            <form>
                                <div className="grid gap-6">
                                    <div className="flex flex-col gap-4">
                                        <Button variant="outline" className="w-full" onClick={async (event) => {
                                            event.preventDefault();
                                            setLoginError(null);
                                            const provider = new GoogleAuthProvider();

                                            try {
                                                if (user?.isAnonymous) {
                                                    console.log('Linking anonymous account to Google...');
                                                    if (isMobile) {
                                                        await linkWithRedirect(user, provider);
                                                    } else {
                                                        await linkWithPopup(user, provider);
                                                        Analytics.userSignup('google_link');
                                                    }
                                                } else {
                                                    if (isMobile) {
                                                        await signInWithRedirect(auth, provider);
                                                    } else {
                                                        await signInWithPopup(auth, provider);
                                                        Analytics.userLogin('google');
                                                    }
                                                }
                                            } catch (error: any) {
                                                logError(error, {
                                                    component: 'LoginPage',
                                                    action: user?.isAnonymous ? 'link_account' : 'sign_in',
                                                    additionalInfo: { provider: 'google' }
                                                });
                                                console.error('Login error:', error);
                                                if (error.code === 'auth/credential-already-in-use') {
                                                    setLoginError("This Google account is already linked to another BibleGlen user. Sign in directly to access that account (guest progress will not be merged).");
                                                } else {
                                                    setLoginError(error.message);
                                                }
                                            }
                                        }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                <path
                                                    d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                            Sign in with Google
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                    <p className="text-center text-sm text-muted-foreground flex flex-col gap-2">
                        <a href="/app/read" className="hover:text-foreground hover:underline transition-colors">
                            ← Continue exploring as a guest
                        </a>
                    </p>
                </div>
            </div>
        </div>
    </>;
}